import { atom } from "../app-jotai";
import type { AuthUser } from "@restack/shared";

const TOKEN_STORAGE_KEY = "excalico_access_token";
const REFRESH_TOKEN_STORAGE_KEY = "excalico_refresh_token";
const USER_STORAGE_KEY = "excalico_user";

export interface AuthSession {
  user: AuthUser | null;
  accessToken: string | null;
  isLoading: boolean;
  error: string | null;
}

const getStoredSession = (): { user: AuthUser | null; token: string | null } => {
  try {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const userStr = localStorage.getItem(USER_STORAGE_KEY);
    const user = userStr ? (JSON.parse(userStr) as AuthUser) : null;
    return { token, user };
  } catch {
    return { token: null, user: null };
  }
};

const initialStored = getStoredSession();

export const authSessionAtom = atom<AuthSession>({
  user: initialStored.user,
  accessToken: initialStored.token,
  isLoading: false,
  error: null,
});

export const authDialogStateAtom = atom<{
  isOpen: boolean;
  initialTab: "login" | "register";
}>({
  isOpen: false,
  initialTab: "login",
});

export const canvasListDialogStateAtom = atom<{
  isOpen: boolean;
}>({
  isOpen: false,
});

export const saveCanvasDialogStateAtom = atom<{
  isOpen: boolean;
}>({
  isOpen: false,
});

export interface ActiveCanvasInfo {
  id: string;
  name: string;
}

const ACTIVE_CANVAS_STORAGE_KEY = "excalico_active_canvas";

const getStoredActiveCanvas = (): ActiveCanvasInfo | null => {
  try {
    const raw = localStorage.getItem(ACTIVE_CANVAS_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ActiveCanvasInfo) : null;
  } catch {
    return null;
  }
};

const _activeCanvasAtom = atom<ActiveCanvasInfo | null>(getStoredActiveCanvas());

export const activeCanvasAtom = atom(
  (get) => get(_activeCanvasAtom),
  (_get, set, update: ActiveCanvasInfo | null) => {
    set(_activeCanvasAtom, update);
    try {
      if (update) {
        localStorage.setItem(ACTIVE_CANVAS_STORAGE_KEY, JSON.stringify(update));
      } else {
        localStorage.removeItem(ACTIVE_CANVAS_STORAGE_KEY);
      }
    } catch {
      // Ignore storage errors
    }
  },
);

export interface CloudSaveStatus {
  status: "idle" | "saving" | "saved" | "error";
  message: string | null;
  timestamp: number | null;
}

export const cloudSaveStatusAtom = atom<CloudSaveStatus>({
  status: "idle",
  message: null,
  timestamp: null,
});

export const authStorage = {
  save: (token: string, user: AuthUser, refreshToken?: string) => {
    try {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
      if (refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
      }
    } catch (e) {
      console.error("Failed to persist auth session to localStorage", e);
    }
  },
  getAccessToken: (): string | null => {
    try {
      return localStorage.getItem(TOKEN_STORAGE_KEY);
    } catch {
      return null;
    }
  },
  getRefreshToken: (): string | null => {
    try {
      return localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
    } catch {
      return null;
    }
  },
  clear: () => {
    try {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem(USER_STORAGE_KEY);
      localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
      localStorage.removeItem(ACTIVE_CANVAS_STORAGE_KEY);
    } catch (e) {
      console.error("Failed to remove auth session from localStorage", e);
    }
  },
};


